import useAuth from "./useAuth";
import { config } from "../Environment";

const useRefreshToken = () => {
  const { setAuth } = useAuth();

  const refresh = async () => {
    const storedAuthJson = localStorage.getItem("auth") ?? "{}";
    const storedAuth = JSON.parse(storedAuthJson);

    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const response = await fetch(config.API_BASE + "/api/auth/refresh", {
          method: "GET",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          const updatedAuth =
            data.email === storedAuth.email
              ? { ...storedAuth, ...data }
              : { ...data };

          setAuth(updatedAuth); // { email, accessToken, roles }
          return data.accessToken;
        }

        // 401/403 = token truly expired or invalid → clear auth, stop retrying
        if (response.status === 401 || response.status === 403) {
          console.warn(`[useRefreshToken] Token expired (${response.status}), clearing auth`);
          setAuth({});
          throw new Error(`Refresh failed: ${response.status}`);
        }

        // 5xx = transient server error → retry with backoff
        if (attempt < 4) {
          console.warn(`[useRefreshToken] Server error ${response.status}, retrying (attempt ${attempt + 1}/5)...`);
          await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }

        // Server error persisted through all retries — do NOT clear auth
        // The token is likely still valid, the server is just temporarily down
        console.warn(`[useRefreshToken] Server error ${response.status} after 5 attempts, keeping auth state`);
        throw new Error(`Refresh failed with status: ${response.status}`);
      } catch (e) {
        // Re-throw if it's our own "Refresh failed" error
        if (e.message?.includes("Refresh failed")) throw e;

        // Network error (server unreachable) → retry with backoff
        if (attempt < 4) {
          console.warn(`[useRefreshToken] Network error, retrying (attempt ${attempt + 1}/5)...`, e.message);
          await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }

        // Final attempt failed with network error — do NOT clear auth
        // Server is temporarily unreachable (deployment, network issue)
        // The refresh token cookie and localStorage auth are still valid
        console.error("[useRefreshToken] Refresh failed after 5 attempts (network error), keeping auth state");
        throw new Error("Refresh failed after retries");
      }
    }

    // Should not reach here, but safety net — do NOT clear auth
    throw new Error("Refresh failed after retries");
  };

  return refresh;
};

export default useRefreshToken;
